<template>
  <v-menu
    v-model="display"
    :close-on-content-click="false"
    :close-on-click="false"
    transition="scale-transition"
    offset-y
    :top="embedPositioningHack && !$vuetify.breakpoint.mobile"
    min-width="250px"
    style="z-index: 1000"
  >
    <!-- :menu-events We don't use v-menu's event bindings, but do them manually
         to improve keyboard navigation. -->
    <template v-slot:activator="{ attrs }">
      <v-text-field
        :style="colorOfInputElementsStyle"
        ref="textfield"
        v-bind="{ ...attrs, ...computedTextFieldProps }"
        :disabled="disabled || pageLoading"
        :label="label"
        v-model="textValue"
        :error-messages="computedErrorMessages"
        :clearable="!timeOnly"
        filled
        @click:clear="clearHandler"
        @click:prepend-inner="toggle"
        @blur="commitText"
        @keydown.enter.prevent="commitText"
        @keydown.tab="resetPicker"
        @keydown.escape="resetPicker"
      ></v-text-field>
    </template>

    <!-- see :menu-events -->
    <v-card
      v-click-outside="{
        handler: () => (date ? okHandler() : resetPicker()),
        closeConditional: () => display,
        include: () => [$refs.textfield.$el],
      }"
    >
      <!-- customer-specific ID (md5) for tracking unlicensed use -->
      <span data-id="@@CUSTOMER_ID@@" />
      <v-card-text class="px-0 py-0">
        <!-- time-only mode: the date is fixed (e.g. preset by the instance config),
             so only the clock is offered -->
        <v-time-picker
          v-if="timeOnly"
          ref="timer"
          v-model="time"
          v-bind="timePickerProps"
          @click:minute="okHandler"
          scrollable
          width="250"
          format="24hr"
        ></v-time-picker>
        <v-tabs v-else fixed-tabs v-model="activeTab">
          <v-tab key="calendar">
            <v-icon>event</v-icon>
          </v-tab>
          <v-tab key="timer" :disabled="!dateSelected">
            <v-icon>access_time</v-icon>
          </v-tab>

          <v-tab-item key="calendar">
            <v-date-picker
              ref="calendar"
              v-model="date"
              v-bind="datePickerProps"
              @input="showTimePicker"
              first-day-of-week="1"
              no-title
            ></v-date-picker>
          </v-tab-item>

          <v-tab-item key="timer">
            <v-time-picker
              ref="timer"
              v-model="time"
              v-bind="timePickerProps"
              @click:minute="okHandler"
              scrollable
              width="250"
              format="24hr"
            ></v-time-picker>
          </v-tab-item>
        </v-tabs>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script>
import format from "date-fns/format";
import parse from "date-fns/parse";

const DEFAULT_DATE = "";
const DEFAULT_TIME = "12:00:00";
const DEFAULT_DATE_FORMAT = "yyyy-MM-dd";
const DEFAULT_TIME_FORMAT = "HH:mm:ss";

export default {
  name: "DateTimePicker",
  model: {
    prop: "datetime",
    event: "input",
  },
  props: {
    pageLoading: { type: Boolean, default: false},
    datetime: { type: [Date, String], default: null },
    disabled: { type: Boolean },
    label: { type: String, default: "" },
    textFieldProps: { type: Object },
    datePickerProps: { type: Object },
    timePickerProps: { type: Object },
    dateFormat: { type: String, default: DEFAULT_DATE_FORMAT },
    timeFormat: { type: String, default: "HH:mm" },
    // show and edit only the time; the date part of `datetime` is kept as is
    timeOnly: { type: Boolean, default: false },
    embedPositioningHack: { type: Boolean },
    colorOfInputElementsStyle: {type: Object, default: ()=>({})}
  },
  data() {
    return {
      display: false,
      lastToggle: 0,
      activeTab: 0,
      changeSinceOpen: false,
      date: DEFAULT_DATE,
      time: DEFAULT_TIME,
      // F-019: lokaler Text-State, damit das Feld tippbar wird
      textValue: "",
      parseError: "",
    };
  },
  mounted() {
    this.init();
  },
  computed: {
    dateTimeFormat() {
      return this.dateFormat + " " + this.timeFormat;
    },
    formattedDatetime() {
      return this.selectedDatetime
        ? format(
            this.selectedDatetime,
            this.timeOnly ? this.timeFormat : this.dateTimeFormat
          )
        : "";
    },
    selectedDatetime() {
      if (!this.date || !this.time) return null;

      let datetimeString = `${this.date} ${this.time}`;
      if (this.time.length === 5) datetimeString += ":00";
      return parse(
        datetimeString,
        `${DEFAULT_DATE_FORMAT} ${DEFAULT_TIME_FORMAT}`,
        new Date()
      );
    },
    dateSelected() {
      return !!this.date;
    },
    /**
     * F-019: textFieldProps ohne errorMessages weiterreichen, damit die externe
     * Fehlermeldung (Searchbar: dateRangeInvalid) mit dem internen Parse-Fehler
     * korrekt zusammengefuehrt werden kann.
     */
    computedTextFieldProps() {
      if (!this.textFieldProps) return {};
      const { errorMessages, ...rest } = this.textFieldProps;
      return rest;
    },
    /**
     * F-019: externe und interne Fehlermeldungen kombinieren.
     */
    computedErrorMessages() {
      const external =
        (this.textFieldProps && this.textFieldProps.errorMessages) || [];
      const arr = Array.isArray(external)
        ? [...external]
        : external
        ? [external]
        : [];
      if (this.parseError) arr.push(this.parseError);
      return arr;
    },
  },
  methods: {
    init() {
      if (!this.datetime) return;
      let initDateTime;
      if (this.datetime instanceof Date) initDateTime = this.datetime;
      else if (
        typeof this.datetime === "string" ||
        this.datetime instanceof String
      )
        initDateTime = parse(this.datetime, this.dateTimeFormat, new Date());

      this.date = format(initDateTime, DEFAULT_DATE_FORMAT);
      this.time = format(initDateTime, DEFAULT_TIME_FORMAT);
      this.resetPicker();
    },
    toggle() {
      // debounce simultaneously firing focus & click events.
      const n = Date.now();
      if (n - this.lastToggle <= 400) return;
      this.lastToggle = n;

      if (this.display) this.resetPicker();
      else this.open();
    },
    open() {
      this.changeSinceOpen = false;
      this.display = true;
    },
    okHandler() {
      if (this.changeSinceOpen) this.$emit("input", this.selectedDatetime);
      this.$refs.textfield.focus();
      this.resetPicker();
    },
    clearHandler() {
      this.date = DEFAULT_DATE;
      this.time = DEFAULT_TIME;
      this.textValue = "";
      this.parseError = "";
      this.$emit("input", null);
      this.resetPicker();
    },
    /**
     * F-019: getippten Wert beim Verlassen / Enter parsen.
     * Akzeptiert "dd.MM.yyyy HH:mm" sowie reines Datum "dd.MM.yyyy"
     * (Uhrzeit faellt dann auf 12:00 zurueck).
     */
    commitText() {
      const value = (this.textValue || "").trim();
      if (this.timeOnly) {
        this.commitTimeOnlyText(value);
        return;
      }
      if (!value) {
        this.parseError = "";
        if (this.date || this.time !== DEFAULT_TIME) {
          this.date = DEFAULT_DATE;
          this.time = DEFAULT_TIME;
          this.$emit("input", null);
        }
        return;
      }
      const formats = [this.dateTimeFormat, this.dateFormat];
      for (const fmt of formats) {
        try {
          const parsed = parse(value, fmt, new Date());
          if (parsed && !isNaN(parsed.getTime())) {
            this.date = format(parsed, DEFAULT_DATE_FORMAT);
            // bei reinem Datumsformat behalten wir die bisherige Uhrzeit bzw. 12:00
            if (fmt === this.dateTimeFormat) {
              this.time = format(parsed, DEFAULT_TIME_FORMAT);
            } else if (!this.time || this.time === "") {
              this.time = DEFAULT_TIME;
            }
            this.parseError = "";
            this.$emit("input", this.selectedDatetime);
            // sicherstellen, dass das angezeigte Textfeld der formatierten Form entspricht
            this.textValue = this.formattedDatetime;
            return;
          }
        } catch (e) {
          // weiter zum naechsten Format
        }
      }
      this.parseError = `Bitte im Format ${this.dateTimeFormat} eingeben.`;
    },
    /**
     * time-only mode: parse a typed "HH:mm" and keep the fixed date; an empty
     * input restores the current value instead of clearing the field
     */
    commitTimeOnlyText(value) {
      if (!value) {
        this.parseError = "";
        this.textValue = this.formattedDatetime;
        return;
      }
      const parsed = parse(value, this.timeFormat, new Date());
      if (parsed && !isNaN(parsed.getTime()) && this.date) {
        this.time = format(parsed, DEFAULT_TIME_FORMAT);
        this.parseError = "";
        this.$emit("input", this.selectedDatetime);
        this.textValue = this.formattedDatetime;
        return;
      }
      this.parseError = `Bitte im Format ${this.timeFormat} eingeben.`;
    },
    resetPicker() {
      this.display = false;
      this.activeTab = 0;
      if (this.$refs.timer) this.$refs.timer.selectingHour = true;
    },
    showTimePicker() {
      this.activeTab = 1;
    },
  },
  watch: {
    datetime: function () {
      this.init();
    },
    date() {
      this.changeSinceOpen = true;
    },
    time() {
      this.changeSinceOpen = true;
    },
    /**
     * F-019: bei externer Aenderung (z.B. Auswahl aus dem Picker) den
     * Text-Wert nachfuehren. Wird der Wert ausschliesslich getippt,
     * aendert sich formattedDatetime erst beim commitText – daher kein
     * unerwuenschter Overwrite waehrend des Tippens.
     */
    formattedDatetime: {
      immediate: true,
      handler(val) {
        if (val !== this.textValue) {
          this.textValue = val || "";
        }
      },
    },
  },
};
</script>

<style>
.v-picker__title {
  padding: 8px !important; /* there is no sass var for this.. */
}

.v-time-picker-clock__container {
  flex-basis: 0 !important; /* override hardcoded value to make sure the clock is round (see width=250) */
}
</style>
